/**
 * Property types: the vocabulary of reflection (SPEC §4.2).
 *
 * A component schema describes each prop with a PropType. That one description
 * drives four consumers with no per-component code: the Inspector (a widget per
 * type), serialization (defaults), command validation, and render translation.
 *
 * Adding a prop type means adding a handler here (validate + default) and an
 * Inspector widget. Nothing else changes.
 */
import { parseHexRgb } from "../../core/color";
import type { Rgb } from "../../core/color";
import type { Vec3 } from "../../core/math/vec3";

/** Fields shared by every prop type. */
interface PropBase {
  /** Inspector label. Defaults to the prop key. */
  readonly label?: string;
  /** The prop may be absent (the Inspector hides empty optional fields). */
  readonly optional?: boolean;
  /** Shown but not editable in the Inspector. */
  readonly readonly?: boolean;
  /**
   * Shown only while an `assetRef` prop of the same component holds an asset
   * of `kind` (a Mesh Renderer's Size applies to engine shapes, not models).
   * The value is kept either way.
   */
  readonly shownWhen?: { readonly assetProp: string; readonly kind: string };
}

export interface EnumOption {
  readonly value: string;
  readonly label: string;
}

export type PropType =
  | (PropBase & { readonly type: "string"; readonly default?: string })
  | (PropBase & { readonly type: "text"; readonly default?: string })
  | (PropBase & {
      readonly type: "number";
      readonly min?: number;
      readonly max?: number;
      readonly step?: number;
      /** Display unit, e.g. "m" or "°". */
      readonly unit?: string;
      readonly default?: number;
    })
  | (PropBase & { readonly type: "bool"; readonly default?: boolean })
  | (PropBase & {
      readonly type: "vec3";
      readonly step?: number;
      readonly unit?: string;
      readonly default?: Vec3;
    })
  | (PropBase & { readonly type: "color"; readonly default?: ColorValue })
  | (PropBase & {
      readonly type: "enum";
      readonly options: readonly EnumOption[];
      readonly default?: string;
    })
  | (PropBase & {
      readonly type: "yearMonth";
      readonly nullable?: boolean;
      /** Label for null, e.g. "Present". */
      readonly nullLabel?: string;
      /** Render together with another yearMonth prop as a date range. */
      readonly pairWith?: string;
    })
  | (PropBase & { readonly type: "array"; readonly of: PropType })
  | (PropBase & {
      readonly type: "assetRef";
      /** The asset kinds it takes: loader kinds (`gltf`) or the engine's (`shape`). */
      readonly kinds: readonly string[];
    })
  | (PropBase & {
      readonly type: "clipRef";
      /**
       * Where the model comes from: an `assetRef` prop on a component of the
       * same entity (e.g. MeshRenderer.mesh). The Inspector lists its clips.
       */
      readonly source: ClipSource;
    })
  | (PropBase & { readonly type: "entityRef"; readonly requires?: string });

export type PropTypeName = PropType["type"];

/** An `assetRef` prop on the same entity, named by component type and prop key. */
export interface ClipSource {
  readonly component: string;
  readonly prop: string;
}

/**
 * A color prop value: an sRGB triple in 0..1, or a reference to a theme's
 * viewport token (e.g. the key light follows `viewport.keyLightTint`).
 */
export type ColorValue = Rgb | { readonly theme: string };

export type PropResult = { ok: true; value: unknown } | { ok: false; error: string };

interface PropHandler<T extends PropType> {
  validate(type: T, value: unknown): PropResult;
  defaultValue(type: T): unknown;
}

type Handlers = { [K in PropTypeName]: PropHandler<Extract<PropType, { type: K }>> };

const YEAR_MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

const HANDLERS: Handlers = {
  string: textHandler(),
  text: textHandler(),

  number: {
    validate: (type, value) => {
      if (typeof value !== "number" || !Number.isFinite(value)) return fail("expected a number");
      if (type.min !== undefined && value < type.min) return fail(`must be ≥ ${type.min}`);
      if (type.max !== undefined && value > type.max) return fail(`must be ≤ ${type.max}`);
      return pass(value);
    },
    defaultValue: (type) => type.default ?? clamp(0, type.min, type.max),
  },

  bool: {
    validate: (_type, value) =>
      typeof value === "boolean" ? pass(value) : fail("expected true or false"),
    defaultValue: (type) => type.default ?? false,
  },

  vec3: {
    validate: (_type, value) =>
      isVec3(value) ? pass([value[0], value[1], value[2]]) : fail("expected [x, y, z]"),
    defaultValue: (type) => type.default ?? [0, 0, 0],
  },

  color: {
    validate: (_type, value) => validateColor(value),
    defaultValue: (type) => type.default ?? [1, 1, 1],
  },

  enum: {
    validate: (type, value) =>
      type.options.some((option) => option.value === value)
        ? pass(value)
        : fail(`must be one of: ${type.options.map((o) => o.value).join(", ")}`),
    defaultValue: (type) => type.default ?? type.options[0]?.value ?? "",
  },

  yearMonth: {
    validate: (type, value) => {
      if (value === null) return type.nullable ? pass(null) : fail("a date is required");
      return typeof value === "string" && YEAR_MONTH.test(value)
        ? pass(value)
        : fail("expected YYYY-MM");
    },
    defaultValue: (type) => (type.nullable ? null : "2000-01"),
  },

  array: {
    validate: (type, value) => {
      if (!Array.isArray(value)) return fail("expected a list");
      const items: unknown[] = [];
      for (const [i, item] of value.entries()) {
        const result = validateProp(type.of, item);
        if (!result.ok) return fail(`[${i}]: ${result.error}`);
        items.push(result.value);
      }
      return pass(items);
    },
    defaultValue: () => [],
  },

  assetRef: referenceHandler(),
  entityRef: referenceHandler(),

  // A clip name in the source model; "" plays its first clip. Names aren't
  // checked here: the model may not have loaded, and a missing clip is a warning.
  clipRef: {
    validate: (_type, value) =>
      typeof value === "string" ? pass(value) : fail("expected a clip name"),
    defaultValue: () => "",
  },
};

/** Validates (and, for colors, normalizes) a value against its prop type. */
export function validateProp(type: PropType, value: unknown): PropResult {
  if (value === undefined) return type.optional ? pass(undefined) : fail("is required");
  return (HANDLERS[type.type] as PropHandler<PropType>).validate(type, value);
}

/** The value a prop takes when a manifest omits it. */
export function defaultForProp(type: PropType): unknown {
  if (type.optional) return undefined;
  return (HANDLERS[type.type] as PropHandler<PropType>).defaultValue(type);
}

// ─── Handler helpers ─────────────────────────────────────────────────────

function textHandler<T extends Extract<PropType, { type: "string" | "text" }>>(): PropHandler<T> {
  return {
    validate: (_type, value) => (typeof value === "string" ? pass(value) : fail("expected text")),
    defaultValue: (type) => type.default ?? "",
  };
}

/** Asset and entity references are ids (strings), or null when unset. */
function referenceHandler<
  T extends Extract<PropType, { type: "assetRef" | "entityRef" }>,
>(): PropHandler<T> {
  return {
    validate: (_type, value) =>
      value === null || (typeof value === "string" && value !== "")
        ? pass(value)
        : fail("expected an id or null"),
    defaultValue: () => null,
  };
}

function validateColor(value: unknown): PropResult {
  if (typeof value === "string") {
    const rgb = parseHexRgb(value);
    return rgb ? pass(rgb) : fail("expected #RRGGBB, [r, g, b] or { theme }");
  }
  if (isVec3(value)) {
    return value.every((c) => c >= 0 && c <= 1)
      ? pass([value[0], value[1], value[2]])
      : fail("channels must be 0..1");
  }
  if (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { theme?: unknown }).theme === "string"
  ) {
    return pass({ theme: (value as { theme: string }).theme });
  }
  return fail("expected #RRGGBB, [r, g, b] or { theme }");
}

function isVec3(value: unknown): value is Vec3 {
  return (
    Array.isArray(value) &&
    value.length === 3 &&
    value.every((c) => typeof c === "number" && Number.isFinite(c))
  );
}

function clamp(value: number, min: number | undefined, max: number | undefined): number {
  return Math.min(max ?? Infinity, Math.max(min ?? -Infinity, value));
}

function pass(value: unknown): PropResult {
  return { ok: true, value };
}

function fail(error: string): PropResult {
  return { ok: false, error };
}
